import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { to: "/", label: "首页" },
  { to: "/category?type=movie", label: "电影" },
  { to: "/category?type=tv", label: "电视剧" },
  { to: "/news", label: "影视资讯" },
];

const QUICK_ENTRIES = [
  { to: "/category?type=movie", label: "电影", icon: "ri-film-line" },
  { to: "/category?type=tv", label: "电视剧", icon: "ri-tv-2-line" },
  { to: "/category?region=国产", label: "国产", icon: "ri-home-heart-line" },
  { to: "/category?region=欧美", label: "欧美", icon: "ri-earth-line" },
  { to: "/category?region=港台", label: "港台", icon: "ri-building-2-line" },
  { to: "/category?region=日韩", label: "日韩", icon: "ri-map-pin-line" },
  { to: "/category?sort=rating", label: "高分", icon: "ri-award-line" },
];

export default function SiteNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setMenuOpen(false);
    setSearchOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    const [path, query] = to.split("?");
    if (query) {
      return (
        location.pathname === path &&
        new URLSearchParams(location.search).toString() === query
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
              <i className="ri-film-line text-white text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg text-foreground-950 tracking-tight">
                影视影院
              </span>
              <span className="text-[10px] text-foreground-400 tracking-[0.2em] mt-0.5">
                MOVIECINEMA
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-md text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(link.to)
                    ? "text-primary-700 bg-primary-100"
                    : "text-foreground-700 hover:text-primary-700 hover:bg-primary-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop search */}
          <form
            onSubmit={submitSearch}
            className="hidden md:flex items-center w-72 h-10 rounded-md border border-background-300 bg-background-100 focus-within:border-primary-400 transition-colors"
          >
            <span className="w-10 h-10 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、电视剧或资讯"
              className="flex-1 h-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="h-8 mr-1 px-3 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          {/* Mobile actions */}
          <div className="flex md:hidden items-center gap-1">
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-xl" />
            </button>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
            </button>
          </div>
        </div>

        {/* Mobile search bar */}
        {searchOpen && (
          <form onSubmit={submitSearch} className="md:hidden pb-3 animate-fade-in">
            <div className="flex items-center w-full h-11 rounded-md border border-background-300 bg-background-100 focus-within:border-primary-400">
              <span className="w-10 h-11 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、电视剧或资讯"
                className="flex-1 h-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="h-9 mr-1 px-4 rounded-md bg-accent-500 text-white text-sm font-label font-medium whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-4">
            <nav className="flex flex-col">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-3 rounded-md text-sm font-label font-medium transition-colors cursor-pointer ${
                    isActive(link.to)
                      ? "text-primary-700 bg-primary-100"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="mt-3 pt-3 border-t border-background-200">
              <p className="text-xs text-foreground-400 mb-2 px-3">快捷分类</p>
              <div className="grid grid-cols-4 gap-2">
                {QUICK_ENTRIES.map((entry) => (
                  <Link
                    key={entry.to}
                    to={entry.to}
                    className="flex flex-col items-center gap-1.5 py-2.5 rounded-md bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center text-primary-600">
                      <i className={`${entry.icon} text-lg`} />
                    </span>
                    <span className="text-xs text-foreground-700">{entry.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}